<script setup>
import { reactive, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router'
import { Button, Toggle, Pagination, Sort, Spin, Text } from '@components'
import { useWidgets } from '@store/widgets';
import { useModal } from '@store/modal'
import { useUpgrade } from '@store/upgrade'
import { useTranslation } from '../composables/useTranslation';
import moment from 'moment';

const { t } = useTranslation();

const $widget = useWidgets();
const $modal = useModal()
const router = useRouter()
const formychat_admin_vars = window.formychat_admin_vars || {}

onMounted(async () => {
    if (formychat_admin_vars.total.widgets == 0) {
        router.push({ name: 'start' })
    } else {
        await $widget.fetchWidgets()
    }
})

const deleteWidget = async (widget) => {
    const isDelete = await $modal.prompt({
        title: widget.is_active ? t('widget_in_use') : t('delete_widget'),
        message: widget.is_active ? t('confirm_delete_active_widget') : t('confirm_delete_widget'),
    })

    if (!isDelete.ok) return;
    const widgets = await $widget.deleteWidgets([widget.id])

    if (widgets.length == 0) {
        // console.log('No widgets left');
        formychat_admin_vars.total.widgets = 0
        router.push({ name: 'start' })
    }
}

const state = reactive({
    duplicating: false,
    error: ''
})
const duplicate = ref(null)

const promptWidgetDuplicate = async (widget) => {
    if (!formychat_admin_vars.is_premium) {
        useUpgrade().show()
        return;
    }

    duplicate.value = {
        ...widget,
        id: null,
        name: t('copy_of', { name: widget.name }),
    }

    setTimeout(() => {
        document.getElementById('duplicate_widget_name')?.focus()
    }, 100)
}

const duplicateWidget = async () => {9
    state.duplicating = true
    await $widget.clone(duplicate.value);
}

</script>
<template>
    <div class="w-full h-full flex flex-col gap-8">
        <!-- header  -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 class="font-medium text-base whitespace-nowrap">{{ t('floating_widgets') }}</h3>
            <div class="flex items-center sm:justify-end gap-2 font-medium">
                <Button
                    @click.prevent="formychat_admin_vars.is_premium || $widget.getWidgets.length === 0 ? $widget.editWidget('new') : useUpgrade().show()"
                    outline class="gap-2"><svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-3.5 "
                        viewBox="0 0 16 16">
                        <path fill-rule="evenodd"
                            d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2" />
                    </svg> {{ t('create_new') }}</Button>
                <Button v-if="!formychat_admin_vars.is_premium" class="formychat-locked"> <svg
                        xmlns="http://www.w3.org/2000/svg" class="fill-current w-3.5" viewBox="0 0 16 16">
                        <path
                            d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641z" />
                    </svg> {{ t('upgrade_now') }}</Button>
            </div>
        </div>
        <div v-if="$widget.isLoading" class="w-full h-full">
            <div class="flex items-center justify-center gap-4 h-40">
                <div class="w-5 h-5 border-2 border-y-transparent border-indigo-500 animate-spin rounded-full"></div>
                <span class="text-base text-slate-500">{{ t('loading_widgets') }}</span>
            </div>
        </div>
        <div v-else class="w-full">
            <!-- table  -->
            <div
                class="w-full overflow-x-auto sm:overflow-visible border border-indigo-100 rounded child:w-max child:sm:w-full child:overflow-x-scroll child:sm:overflow-x-visible">
                <!-- table head  -->
                <div
                    class="rounded-t-sm flex whitespace-nowrap sm:sticky top-7 z-20 items-center border-b border-indigo-100 bg-indigo-100 py-2 px-5 font-semibold">
                    <div class="w-24">{{ t('status') }}</div>
                    <div class="w-1/6 cursor-pointer flex items-center gap-2" @click="$widget.setSort('name')">{{ t('widget_name') }}
                        <Sort :asc="$widget.filters.sortBy == 'name' && $widget.filters.sortOrder == 'asc'" />
                    </div>
                    <div class="w-1/6 cursor-pointer flex items-center gap-2">{{ t('whatsapp') }}</div>
                    <div class="w-1/6 cursor-pointer flex items-center gap-2">{{ t('form_mode') }}</div>
                    <div class="w-1/6 cursor-pointer flex items-center gap-2" @click="$widget.setSort('created_at')">
                        {{ t('created_at') }}
                        <Sort :asc="$widget.filters.sortBy == 'created_at' && $widget.filters.sortOrder == 'asc'" />
                    </div>
                    <div class="w-32">{{ t('actions') }}</div>
                </div>
                <!-- table body -->
                <div v-for="widget in $widget.getWidgets" :key="widget.id" class="w-full h-fit">
                    <transition name="fade">
                        <div v-if="!widget.deleted"
                            class="flex items-center border-b border-indigo-100 py-4 px-5 transition hover:bg-slate-50 last:rounded-b-sm"
                            :class="{ 'bg-slate-100 hover:bg-slate-100': !widget.is_active, 'animate-pulse opacity-90 pointer-events-none': widget.statusLoading || widget.is_cloning }">
                            <div class="w-24 text-center">
                                <Toggle v-model="widget.is_active" @change="$widget.update(widget)" />
                            </div>
                            <div class="w-1/6 whitespace-nowrap transition"
                                :class="{ 'opacity-70': !widget.is_active }">{{ widget.name || t('untitled_widget') }}
                            </div>
                            <div class="w-1/6 whitespace-nowrap transition"
                                :class="{ 'opacity-70': !widget.is_active }">+{{ widget.config.whatsapp.country_code
                                }}{{ widget.config.whatsapp.number }}</div>
                            <div class="w-1/6 whitespace-nowrap transition"
                                :class="{ 'opacity-70': !widget.is_active }">{{ widget.config.form.mode === 'cf7' ?
                                t('contact_form_7') : t('formychat')}}</div>
                            <div class="w-1/6 whitespace-nowrap transition"
                                :class="{ 'opacity-70': !widget.is_active }">{{ moment(widget.created_at).calendar() }}
                            </div>
                            <div
                                class="w-32 flex justify-end gap-3 child:h-8 child:w-9 child:rounded child:flex child:items-center child:outline-none child:justify-center child:transition child:cursor-pointer">
                                <button :title="t('edit_widget')" @click.prevent="$widget.editWidget(widget)"
                                    class="bg-indigo-100 text-indigo-600 hover:bg-indigo-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-4"
                                        viewBox="0 0 16 16">
                                        <path
                                            d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z" />
                                    </svg>
                                </button>
                                <button :class="{ 'pointer-events-none': widget.is_cloning || widget.statusLoading }"
                                    :title="t('duplicate_widget')" @click.prevent="promptWidgetDuplicate(widget)"
                                    class="bg-sky-100 text-sky-600 hover:bg-sky-200">
                                    <Spin v-if="widget.is_cloning" />
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="fill-current w-4"
                                        viewBox="0 0 16 16">
                                        <path fill-rule="evenodd"
                                            d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z" />
                                    </svg>
                                </button>
                                <button :title="t('delete_widget')" @click.prevent="deleteWidget(widget)"
                                    class="bg-red-100 text-red-600 hover:bg-red-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-4"
                                        viewBox="0 0 16 16">
                                        <path
                                            d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <div class="h-8"></div> 
            <!-- Duplicate modal  -->
            <transition name="fade">
                <div v-if="duplicate" tabindex="0" @keydown.esc="duplicate = null" @click.self="duplicate = null"
                    
                    class="fixed bg-black/50 z-[99999] w-full h-full flex items-center justify-center left-0 top-0">
                    <div 
                        class="w-full relative max-w-md p-7 rounded bg-white shadow flex flex-col gap-3">
                        <span class="dashicons dashicons-no-alt absolute right-4 top-1 cursor-pointer text-gray-200 hover:text-gray-600 transition duration-75 text-2xl p-1" @click="duplicate = null"></span>
                        <h3 class="font-medium text-base">{{ t('duplicate_widget_title') }}</h3>
                        <p class="text-sm text-gray-500">{{ t('duplicate_widget_message') }}</p>
                        <Text id="duplicate_widget_name" v-model="duplicate.name" :placeholder="t('widget_name')" />
                        <div class="flex items-center justify-end gap-3 mt-3">
                            <Button outline @click.prevent="duplicate = null">{{ t('cancel') }}</Button>
                            <Button @click.prevent="duplicateWidget()">
                                <span v-if="state.duplicating" class="flex items-center justify-center">
                                    <Spin /> {{ t('creating') }}
                                </span>
                                <span v-else>{{ t('create_widget') }}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>
